
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Loader2, ArrowRight, RefreshCw } from 'lucide-react';
import { useSubscription } from '../context/SubscriptionContext';
import { SubscriptionTier } from '../types';
import { getCurrentOrganisationId, getErrorMessage, isMockMode, supabase } from '../services/supabaseClient';

const StripeCallback: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setTier } = useSubscription();
  const [status, setStatus] = useState<'loading' | 'success' | 'pending' | 'error'>('loading');
  const [detail, setDetail] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  // Robust parsing: useSearchParams sometimes acts weird with HashRouter depending on URL structure.
  // Fallback to manual parsing of window.location.hash
  const getParam = (key: string): string | null => {
    // 1. Try React Router standard
    const val = searchParams.get(key);
    if (val) return val;

    // 2. Manual Fallback: Extract from after the '?' in the hash
    const hash = window.location.hash; // e.g., "#/checkout/success?session_id=...&tier=..."
    if (hash.includes('?')) {
      const queryString = hash.split('?')[1];
      const urlParams = new URLSearchParams(queryString);
      return urlParams.get(key);
    }
    return null;
  };

  const tierParam = getParam('tier') as SubscriptionTier;
  const sessionId = getParam('session_id');
  const isSuccess =
    window.location.hash.includes('success') ||
    window.location.pathname.includes('success') ||
    window.location.hash.includes('stripe-callback') ||
    window.location.pathname.includes('stripe-callback');

  useEffect(() => {
    if (!isSuccess) {
      setStatus('error');
      setDetail('Checkout was cancelled or the return URL is invalid.');
      return;
    }

    if (!tierParam) {
      console.error("Missing Tier Parameter in URL");
      setStatus('error');
      setDetail('Missing plan information in the return URL.');
      return;
    }

    // Best-effort activation check: poll organisations.subscription_tier (updated by webhook)
    const verifySession = async () => {
      try {
        if (isMockMode) {
          await new Promise(resolve => setTimeout(resolve, 800));
          setTier(tierParam);
          setStatus('success');
          setTimeout(() => navigate('/'), 2000);
          return;
        }

        setStatus('loading');
        setDetail(sessionId ? `Confirming checkout session ${sessionId}…` : 'Activating your plan…');

        const orgId = await getCurrentOrganisationId();

        const expected = tierParam;
        const started = Date.now();
        const timeoutMs = 45_000;

        while (Date.now() - started < timeoutMs) {
          const { data, error } = await supabase
            .from('organisations')
            .select('subscription_tier')
            .eq('id', orgId)
            .single();
          if (error) throw error;

          const serverTier = (data?.subscription_tier as SubscriptionTier) || 'starter';
          if (serverTier === expected) {
            setTier(serverTier);
            setStatus('success');
            setDetail(null);
            setTimeout(() => navigate('/'), 2500);
            return;
          }

          setStatus('pending');
          setDetail('Payment received. Waiting for subscription activation…');
          await new Promise((r) => setTimeout(r, 3000));
        }

        // Timeout: don’t claim failure; allow the user to continue and re-check later.
        setStatus('pending');
        setDetail('Checkout completed, but activation is still processing. You can continue and we’ll update once it’s confirmed.');
      } catch (err) {
        console.error(err);
        setStatus('error');
        setDetail(getErrorMessage(err));
      }
    };

    void verifySession();
  }, [attempt, isSuccess, sessionId, tierParam, setTier, navigate]);

  if (status === 'loading') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
        <h2 className="text-xl font-bold text-slate-800 dark:text-white">Confirming checkout…</h2>
        <p className="text-slate-500 dark:text-slate-400">{detail || 'Please do not close this window.'}</p>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
        <XCircle className="w-16 h-16 text-red-500 mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Checkout confirmation failed</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-md">
          {detail || "We couldn't confirm your checkout. If you were charged, your plan may still activate shortly."}
        </p>
        <button 
          onClick={() => navigate('/pricing')}
          className="px-6 py-2 bg-slate-900 text-white rounded-lg hover:bg-slate-800 transition-colors"
        >
          Return to Pricing
        </button>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950 px-6">
        <Loader2 className="w-12 h-12 text-primary-600 animate-spin mb-4" />
        <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Activation in progress</h2>
        <p className="text-slate-500 dark:text-slate-400 mb-6 text-center max-w-md">
          {detail || 'We’re waiting for Stripe to confirm your subscription.'}
        </p>
        <div className="flex gap-3">
          <button
            onClick={() => setAttempt((x) => x + 1)}
            className="px-6 py-2 bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 text-slate-800 dark:text-white rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors flex items-center gap-2"
          >
            <RefreshCw size={16} />
            Refresh status
          </button>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
          >
            Continue <ArrowRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-950">
      <CheckCircle2 className="w-16 h-16 text-green-500 mb-4" />
      <h2 className="text-2xl font-bold text-slate-800 dark:text-white mb-2">Checkout confirmed</h2>
      <p className="text-slate-500 dark:text-slate-400 mb-6 text-center">
        Your plan is now active: <span className="font-bold uppercase">{tierParam}</span>.
      </p>
      <button 
        onClick={() => navigate('/')}
        className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
      >
        Go to Dashboard <ArrowRight size={16} />
      </button>
    </div>
  );
};

export default StripeCallback;
